/// <reference path="../apple-pay.d.ts" />
'use client';

import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';
import Script from 'next/script';

import { captureException } from '@sentry/nextjs';
import { Calendar, Clock, Users, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  type AppliedDiscount,
  DiscountCodeInput,
} from '@kit/booking-addons/components/discount-code-input';
import { toMinorUnits } from '@kit/shared/currency';
import { formatLocaleDate } from '@kit/shared/utils';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@kit/ui/alert-dialog';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardHeader } from '@kit/ui/card';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { Dialog, DialogContent, DialogTitle } from '@kit/ui/dialog';
import { Input } from '@kit/ui/input';
import { PhoneInput } from '@kit/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { toast } from '@kit/ui/sonner';
import { cn } from '@kit/ui/utils';

import { TenantBeginCheckout } from '~/(tenant)/_components/analytics/tenant-begin-checkout';
import { createPackagePurchaseReference } from '~/(tenant)/_lib/package-purchase-reference';
import { canPayPackageWithWallet } from '~/(tenant)/packages/_lib/package-wallet';
import pathsConfig from '~/config/paths.config';

import { formatPackageLabel } from '../_lib/format-package-label';
import {
  type PublicDiscountCode,
  publicCodesForPackage,
} from '../_lib/public-discount-codes';
import type { SelectedAccount } from '../_lib/types';
import { AccountMultiSelector } from './account-multi-selector';
import { useBookingPresentation } from './booking-presentation';
import { BookingPolicyNotice } from './booking-policy-notice';

/** One captured player for a player-priced package purchase. */
export interface PlayerDetail {
  name: string;
  phone: string;
}

interface PackageCheckoutDialogProps {
  bookingPolicy?: {
    requireAcceptance: boolean;
    cancellationPolicyItems: string[];
    termsText: string;
    purchaseKey: string;
  };
  isOpen: boolean;
  onClose: () => void;
  packageData: {
    id: string;
    name: string;
    price: number;
    booking_count: number;
    validity_months?: number;
    validity_days?: number | null;
    slot_duration?: number;
    original_price?: number | null;
    description?: string | null;
    features?: string[];
    package_type?: string;
    color?: string | null;
    player_count?: number;
    player_pricing?: Record<string, number> | null;
    max_subscribers?: number | null;
    current_subscribers?: number;
    remaining_slots?: number | null;
    is_bundle?: boolean;
    is_fixed_slot?: boolean;
    /** Book-by (false) vs attend-by (true). Must be explicitly false to offer
     *  the optional start-date picker (undefined = unknown = fail closed). */
    require_attendance_within_validity?: boolean;
    /** Fixed validity window (tenant-local dates). Shown instead of the
     *  duration line; its presence disables the start-date picker. */
    fixed_starts_at?: string | null;
    fixed_ends_at?: string | null;
  } | null;
  onCheckout: (
    packageId: string,
    playerCount?: number,
    price?: number,
    selectedAccounts?: SelectedAccount[],
    discountCode?: string,
    playerDetails?: PlayerDetail[],
    /** Customer-chosen validity start (YYYY-MM-DD), only when a future date
     *  was picked — today/empty is omitted (server default). */
    startDate?: string,
  ) => void;
  onWalletCheckout?: (
    packageId: string,
    playerCount: number | undefined,
    price: number | undefined,
    selectedAccounts: SelectedAccount[],
    discountCode: string | undefined,
    playerDetails: PlayerDetail[] | undefined,
    startDate: string | undefined,
    purchaseReference: string,
  ) => Promise<boolean | void>;
  isProcessing?: boolean;
  isAuthenticated?: boolean;
  userId?: string; // Add userId prop for authenticated users
  userName?: string; // User's name for display
  userPhone?: string; // User's phone (E.164) for prefilling Player 1's phone
  tenantId?: string; // Tenant ID for family member filtering
  tenantName?: string; // Tenant slug/name for payment metadata
  publishableKey?: string | null; // Tenant's publishable key for Apple Pay
  /** Active payment provider. The Apple Pay button posts straight to Moyasar,
   *  so it only renders for Moyasar tenants; other providers (Stripe, etc.)
   *  offer Apple Pay inside their own checkout. */
  provider?: string | null;
  locationId?: string; // Package's location_id — routes Apple Pay to per-location merchant
  /** Tenant-advertised public codes — the applicable ones render as one-tap
   *  buttons in the discount input; a lone applicable code is auto-applied. */
  publicDiscountCodes?: PublicDiscountCode[];
  walletEnabled?: boolean;
  walletBalance?: number;
  allowDiscountCodes?: boolean;
  /**
   * Fixed-slot Apple Pay bridge. Apple Pay charges Moyasar directly from the
   * browser, so — unlike the Card button, which defers to the fixed-slot
   * checkout action — the express handler has no recurring-slot metadata of its
   * own. When this is provided, the Apple Pay button is shown for fixed-slot
   * packages and is called (async, inside the Apple Pay authorization) right
   * before charging: it must run the same server-side fixed-slot checkout
   * (validate availability + build `fixed_slot: 'true'` + days/times/coach/
   * start-date metadata) and return that metadata to stamp on the Moyasar
   * charge, or an error to abort the charge cleanly. Without it the express
   * charge would omit the cadence and the webhook would refund — so the button
   * stays hidden for fixed-slot packages when this is absent.
   */
  resolveFixedSlotApplePayMetadata?: (args: {
    playerCount: number;
    selectedAccounts: SelectedAccount[];
    playerDetails?: PlayerDetail[];
  }) => Promise<
    | {
        success: true;
        metadata: Record<string, string>;
        /** Return page for this charge (default: the package return page). */
        returnPath?: string;
      }
    | { success: false; error: string }
  >;
}

const PACKAGE_COLORS = {
  gold: '#B8860B',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
};

// Safely call session.completePayment — Safari 26+ throws TypeError
// when the `errors` array contains plain objects instead of ApplePayError instances.
function safeCompletePayment(session: any, status: number) {
  try {
    session.completePayment({ status });
  } catch {
    // Silently ignore — the Apple Pay sheet will close regardless
  }
}

export function PackageCheckoutDialog({
  isOpen,
  onClose,
  packageData,
  onCheckout,
  onWalletCheckout,
  isProcessing = false,
  isAuthenticated = false,
  userId,
  userName,
  userPhone,
  tenantId,
  tenantName,
  publishableKey,
  provider,
  locationId,
  publicDiscountCodes = [],
  walletEnabled = false,
  walletBalance = 0,
  allowDiscountCodes = true,
  resolveFixedSlotApplePayMetadata,
  bookingPolicy,
}: PackageCheckoutDialogProps) {
  const presentation = useBookingPresentation();
  const router = useRouter();
  const { t, i18n } = useTranslation('bookings');
  const tenantCurrency = useTenantCurrency();
  const supabase = useSupabase();
  const [playerCount, setPlayerCount] = useState<number | null>(null);
  // Captured per-player name + phone for player-priced packages (players 1..N).
  const [playerDetails, setPlayerDetails] = useState<PlayerDetail[]>([]);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isWalletConfirmOpen, setIsWalletConfirmOpen] = useState(false);
  const applePayButtonRef = useRef<HTMLElement | null>(null);
  const walletPurchaseReferenceRef = useRef<{
    intentKey: string;
    reference: string;
  } | null>(null);
  // Keep the checkout payload as an array containing one recipient.
  const [selectedAccounts, setSelectedAccounts] = useState<SelectedAccount[]>(
    [],
  );
  const policyContext = JSON.stringify([
    isOpen,
    tenantId,
    userId,
    packageData?.id,
    packageData?.price,
    bookingPolicy,
    selectedAccounts.map((account) => account.id),
  ]);
  const [policyAcceptance, setPolicyAcceptance] = useState({
    context: policyContext,
    accepted: false,
  });
  if (policyAcceptance.context !== policyContext) {
    setPolicyAcceptance({ context: policyContext, accepted: false });
  }
  const hasAcceptedPolicy =
    policyAcceptance.context === policyContext &&
    policyAcceptance.accepted;
  const policyBlocked = !!bookingPolicy?.requireAcceptance && !hasAcceptedPolicy;
  // Customer discount code (preview-validated; re-validated server-side at checkout)
  const [appliedDiscount, setAppliedDiscount] =
    useState<AppliedDiscount | null>(null);
  // Buyer's code-less granted discount for this package (self-purchase only);
  // the resolver forces auth.uid(), so only the buyer's own grant is returned.
  const [grantedDiscount, setGrantedDiscount] = useState<{
    addonId: string;
    name: string;
    discountType: 'percent' | 'fixed';
    discountValue: number;
  } | null>(null);
  // Optional customer-chosen validity start (YYYY-MM-DD). '' = start today
  // (the field is omitted from the checkout payload). Only offered for book-by
  // packages without a fixed window — see `offersStartDate` below.
  const [startDate, setStartDate] = useState('');
  // Start-date picker stays collapsed until the buyer asks for it (see the
  // render block). Not reset on close: the dialog unmounts its state anyway.
  const [showStartDate, setShowStartDate] = useState(false);

  // Tenant-advertised codes applicable to THIS package — rendered as one-tap
  // buttons; a lone code auto-applies. Display-level narrowing only; the
  // preview/redeem RPCs re-validate.
  const suggestedCodes =
    packageData && allowDiscountCodes
      ? publicCodesForPackage(publicDiscountCodes, {
          id: packageData.id,
          package_type: packageData.package_type ?? null,
          location_id: locationId ?? null,
        }).map((c) => ({
          code: c.code,
          name: c.name,
          discountType: c.discount_type,
          discountValue: Number(c.discount_value),
        }))
      : [];

  // Calculate price based on player count
  const calculatePrice = (count: number | null) => {
    if (!packageData || count === null) return 0;
    if (count === 1 || !packageData.player_pricing) {
      return packageData.price;
    }
    const customPrice = packageData.player_pricing[String(count)];
    return customPrice !== undefined ? customPrice : packageData.price;
  };

  const pricePerPackage = calculatePrice(playerCount);
  const accountCount = Math.max(1, selectedAccounts.length);
  // A grant is personal — it applies only when buying for the buyer alone
  // (server enforces the same single-self-purchase rule on checkout).
  const isSelfPurchase =
    isAuthenticated &&
    !!userId &&
    selectedAccounts.length <= 1 &&
    (selectedAccounts.length === 0 || selectedAccounts[0]?.id === userId);
  // Granted per-unit amount (percent/fixed, same math as kit.compute_discount_amount).
  // A typed code always wins; the grant only fills in when no code is applied.
  const grantedPerUnit =
    !appliedDiscount && isSelfPurchase && grantedDiscount
      ? grantedDiscount.discountType === 'percent'
        ? Math.round(
            ((pricePerPackage * grantedDiscount.discountValue) / 100) * 100,
          ) / 100
        : Math.min(grantedDiscount.discountValue, pricePerPackage)
      : 0;
  const grantedApplied = grantedPerUnit > 0;
  // Per-unit discount, capped at the unit price (the preview RPC already caps,
  // but guard against a stale preview after the player count changes the unit).
  const perUnitDiscount = appliedDiscount
    ? Math.min(appliedDiscount.amountOff, pricePerPackage)
    : grantedPerUnit;
  const discountedUnit = Math.max(0, pricePerPackage - perUnitDiscount);
  // Subtotal before discount, total discount, and the charged total.
  const subtotal = pricePerPackage * accountCount;
  const discountTotal = perUnitDiscount * accountCount;
  // Total price = (discounted) price per package × number of selected accounts
  const displayPrice = discountedUnit * accountCount;

  // Player-detail capture applies only when 2+ players are selected — a single
  // player is the buyer, already identified by their account.
  const shownPlayerRows =
    playerCount !== null && playerCount >= 2 ? playerCount : 0;

  const resizePlayerDetails = (count: number) => {
    setPlayerDetails((prev) =>
      Array.from(
        { length: count >= 2 ? count : 0 },
        (_, i) =>
          prev[i] ?? { name: i === 0 ? (userName ?? '') : '', phone: '' },
      ),
    );
  };

  const updatePlayerDetail = (
    index: number,
    field: 'name' | 'phone',
    value: string,
  ) => {
    setPlayerDetails((prev) => {
      const next = [...prev];
      next[index] = {
        ...(next[index] ?? { name: '', phone: '' }),
        [field]: value,
      };
      return next;
    });
  };

  // Player 1 is always the logged-in account holder, so its name defaults to
  // `userName` whenever the field is left untouched (prefilled). Players 2..N
  // have no default.
  const resolvePlayerName = (index: number) =>
    playerDetails[index]?.name?.trim() ||
    (index === 0 ? (userName ?? '').trim() : '');

  const resolvePlayerPhone = (index: number) =>
    playerDetails[index]?.phone?.trim() ||
    (index === 0 ? (userPhone ?? '').trim() : '');

  const playerDetailsComplete =
    shownPlayerRows === 0 ||
    Array.from({ length: shownPlayerRows }).every(
      (_, i) =>
        resolvePlayerName(i).length > 0 && resolvePlayerPhone(i).length > 0,
    );

  const playerDetailsPayload =
    shownPlayerRows > 0
      ? Array.from({ length: shownPlayerRows }, (_, i) => ({
          name: resolvePlayerName(i),
          phone: resolvePlayerPhone(i),
        }))
      : undefined;

  // Customer-chosen start dates are a use-by (attend-by) -without-window
  // feature only (mirrors the server action's gate): require an EXPLICIT
  // attend-by flag — undefined means the caller didn't load the column (fail
  // closed) — and no fixed-slot / bundle / fixed-window package.
  const offersStartDate =
    !!packageData &&
    packageData.require_attendance_within_validity === true &&
    !packageData.is_fixed_slot &&
    !packageData.is_bundle &&
    !packageData.fixed_starts_at &&
    !packageData.fixed_ends_at;

  // Browser-local today for the input's min; the server re-validates against
  // the tenant's calendar day. en-CA renders YYYY-MM-DD.
  const todayLocal = new Intl.DateTimeFormat('en-CA').format(new Date());

  // Only a FUTURE pick is sent — today/empty means "start now", the server
  // default (the action rejects past dates anyway).
  const startDatePayload =
    offersStartDate && startDate && startDate > todayLocal
      ? startDate
      : undefined;

  // Reset player count + selected accounts when the package changes.
  // Seed `selectedAccounts` with self immediately so Apple Pay / card checkout
  // isn't blocked while AccountMultiSelector is still fetching children in the
  // background. If the user has children, AccountMultiSelector renders and
  // lets them override the selection; if they don't, self stays selected.
  useEffect(() => {
    if (!packageData) return;
    setPlayerCount(packageData.player_pricing ? null : 1);
    setPlayerDetails([]);
    setSelectedAccounts(
      userId
        ? [{ id: userId, name: userName || t('bookForSelf'), isChild: false }]
        : [],
    );
    // Drop any applied code when switching packages — eligibility/scope differ.
    setAppliedDiscount(null);
    // Clear the resolved grant too, else the prior package's grant misprices the
    // new one (incl. Apple Pay) until the async re-resolve below settles.
    setGrantedDiscount(null);
    // Reset the chosen start date — a different package may not even offer one.
    setStartDate('');
    // A purchase reference is stable for retries of one dialog attempt, but a
    // different package must receive a fresh idempotency key.
    walletPurchaseReferenceRef.current = null;
  }, [packageData?.id, packageData?.player_pricing, userId, userName, t]);

  // Resolve the buyer's grant for display + the Apple Pay charge (self only). Skip
  // fixed-slot: its checkout action ignores grants, so a discount here would misprice.
  useEffect(() => {
    if (
      !isOpen ||
      !packageData ||
      packageData.is_fixed_slot ||
      !isAuthenticated ||
      !userId ||
      !tenantId
    ) {
      setGrantedDiscount(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC not yet in generated Database types until migration applied
      const { data, error } = await (supabase.rpc as any)(
        'resolve_granted_discount_for_customer',
        {
          p_account_id: tenantId,
          p_user_id: userId,
          p_for_package: true,
          p_package_id: packageData.id,
          p_package_type: packageData.package_type ?? null,
        },
      );
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (error || !row) {
        setGrantedDiscount(null);
        return;
      }
      setGrantedDiscount({
        addonId: row.addon_id,
        name: row.addon_name,
        discountType: row.discount_type,
        discountValue: Number(row.discount_value),
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    packageData?.id,
    packageData?.package_type,
    isAuthenticated,
    userId,
    tenantId,
    supabase,
  ]);

  const handleSignIn = () => {
    // Store the current URL to redirect back after sign in
    const currentUrl = window.location.pathname;
    router.push(`${pathsConfig.auth.signIn}?next=${currentUrl}`);
  };

  // Handle Apple Pay payment for package purchase
  const handleApplePayClick = async () => {
    if (policyBlocked) {
      toast.error(t('toastAcceptPolicyRequired'));
      return;
    }
    // Check if package data exists
    if (!packageData) {
      toast.error(t('toastPackageDataUnavailable'));
      return;
    }

    // Check if player count is selected for packages with player pricing
    if (packageData.player_pricing && playerCount === null) {
      toast.error(t('toastSelectPlayers'));
      return;
    }

    // Require each player's name + phone when 2+ players are selected
    if (shownPlayerRows > 0 && !playerDetailsComplete) {
      toast.error(t('toastPlayerDetailsIncomplete'));
      return;
    }

    // Check if ApplePaySession exists
    if (!(window as any).ApplePaySession) {
      toast.error(t('toastApplePayUnavailable'));
      return;
    }

    // Apple Pay charges the gateway directly, so validate before starting it.
    if (isAuthenticated && selectedAccounts.length !== 1) {
      toast.error(t('toastSelectAccount'));
      return;
    }

    setIsPaymentProcessing(true);

    // Fixed-slot is single-seat and not discount-aware: the fixed-slot checkout
    // action ignores discounts + extra family selections and fulfils exactly one
    // recipient, so Apple Pay charges the base unit price (matching what the Card
    // path passes as pricePerPackage). Non-fixed-slot uses the full discounted
    // multi-account total. Without this, a discount code or a 2nd selected
    // account makes displayPrice diverge from the server's single-seat price and
    // the fixed-slot price guard below would abort every such charge.
    const applePayAmount = packageData?.is_fixed_slot
      ? pricePerPackage
      : displayPrice;

    const applePayPaymentRequest = {
      countryCode: 'SA',
      currencyCode: tenantCurrency,
      supportedNetworks: ['mada', 'visa', 'masterCard'],
      merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
      total: {
        label:
          selectedAccounts.length > 1
            ? t('applePayPackageMultiAccount', {
                name: packageData.name,
                count: selectedAccounts.length,
              })
            : t('applePayPackageLabel', {
                name: packageData.name,
                count: packageData.booking_count,
              }),
        amount: applePayAmount.toFixed(2),
      },
      // Request contact information from Apple Pay for guest users
      ...(!isAuthenticated && {
        requiredShippingContactFields: ['email', 'phone', 'name'],
      }),
    };

    const session = new (window as any).ApplePaySession(
      5,
      applePayPaymentRequest,
    );

    // Track session lifecycle to distinguish user cancel vs system failure
    let merchantValidated = false;

    // Merchant Validation
    session.onvalidatemerchant = async (event: any) => {
      if (!publishableKey) {
        console.error('Apple Pay: Tenant publishable key not configured');
        toast.error(t('toastPaymentConfigError'));
        session.abort();
        setIsPaymentProcessing(false);
        return;
      }

      const body = {
        validation_url: event.validationURL,
        display_name: t('applePayPackagePurchaseLabel'),
        domain_name: window.location.hostname,
        publishable_api_key: publishableKey,
      };

      try {
        const response = await fetch(
          'https://api.moyasar.com/v1/applepay/initiate',
          {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
        );

        const merchantSession = await response.json();
        session.completeMerchantValidation(merchantSession);
        merchantValidated = true;
      } catch (error) {
        console.error('Merchant validation error:', error);
        captureException(error, {
          tags: {
            payment_method: 'applepay',
            error_type: 'merchant_validation_failed',
            booking_type: 'package',
          },
          extra: {
            domain: window.location.hostname,
            hasPublishableKey: !!publishableKey,
          },
        });
        toast.error(t('toastPaymentValidationFailed'));
        session.abort();
        setIsPaymentProcessing(false);
      }
    };

    // Payment Authorization
    session.onpaymentauthorized = async (event: any) => {
      const token = event.payment.token;
      const shippingContact = event.payment.shippingContact;

      try {
        let finalUserId: string | undefined;
        let customerName = '';
        let customerEmail = '';
        let customerPhone = '';

        // For authenticated users, use the provided userId
        if (isAuthenticated && userId) {
          finalUserId = userId;
        } else if (!isAuthenticated) {
          // Extract contact information from Apple Pay
          customerName = shippingContact?.givenName
            ? `${shippingContact.givenName} ${shippingContact.familyName || ''}`.trim()
            : '';
          customerEmail = shippingContact?.emailAddress || '';
          customerPhone = shippingContact?.phoneNumber || '';

          // Validate required fields
          if (!customerName || !customerPhone) {
            toast.error(t('toastNamePhoneRequired'));
            safeCompletePayment(
              session,
              (window as any).ApplePaySession.STATUS_FAILURE,
            );
            setIsPaymentProcessing(false);
            return;
          }

          // For guest users, create guest account first
          // Use Apple Pay specific action (no CAPTCHA - Apple Pay provides authentication)
          const { createGuestUserFromApplePayAction } =
            await import('../_lib/server/guest-registration-actions');

          const guestResult = await createGuestUserFromApplePayAction({
            name: customerName,
            email: customerEmail,
            phoneNumber: customerPhone,
          });

          if (!guestResult.success || !guestResult.data) {
            toast.error(t('toastGuestAccountFailed'));
            safeCompletePayment(
              session,
              (window as any).ApplePaySession.STATUS_FAILURE,
            );
            setIsPaymentProcessing(false);
            return;
          }

          finalUserId = guestResult.data.userId;
        }

        // Fixed-slot packages: unlike the Card button (which defers to the
        // fixed-slot checkout action), the express charge posts to Moyasar
        // directly and would otherwise carry no recurring-slot metadata — the
        // webhook would then be unable to build the sessions and would refund.
        // Resolve the same server-validated metadata now (this also re-checks
        // availability) and abort the charge cleanly on any problem.
        let fixedSlotMetadata: Record<string, string> | null = null;
        let fixedSlotReturnPath: string | null = null;
        if (packageData.is_fixed_slot) {
          if (!resolveFixedSlotApplePayMetadata) {
            // Defensive: the button should not have rendered without a bridge.
            toast.error(t('toastPaymentProcessingFailed'));
            safeCompletePayment(
              session,
              (window as any).ApplePaySession.STATUS_FAILURE,
            );
            setIsPaymentProcessing(false);
            return;
          }

          const resolved = await resolveFixedSlotApplePayMetadata({
            playerCount: playerCount ?? 1,
            selectedAccounts,
            playerDetails: playerDetailsPayload,
          });

          if (!resolved.success) {
            toast.error(resolved.error);
            safeCompletePayment(
              session,
              (window as any).ApplePaySession.STATUS_FAILURE,
            );
            setIsPaymentProcessing(false);
            return;
          }

          // The Apple Pay sheet total was locked to applePayAmount; only
          // proceed when the server returned a parseable price that matches it
          // (fixed-slot is single-seat, so the two must match). Fail closed: an
          // unparseable/missing price aborts rather than charging unchecked.
          const serverPrice = Number(resolved.metadata.price);
          if (
            !Number.isFinite(serverPrice) ||
            Math.abs(serverPrice - applePayAmount) > 0.001
          ) {
            toast.error(t('toastPaymentProcessingFailed'));
            safeCompletePayment(
              session,
              (window as any).ApplePaySession.STATUS_FAILURE,
            );
            setIsPaymentProcessing(false);
            return;
          }

          fixedSlotMetadata = resolved.metadata;
          fixedSlotReturnPath = resolved.returnPath ?? null;
        }

        // Generate unique purchase ID. Fixed-slot reuses the server-issued id
        // from the resolved metadata so the return page and webhook agree on
        // the reference; everything else uses the shared reference helper.
        const purchaseId =
          fixedSlotMetadata?.purchase_id ?? createPackagePurchaseReference();

        // Get user IDs for multi-account purchase
        const userIds =
          isAuthenticated && selectedAccounts.length > 0
            ? selectedAccounts.map((acc) => acc.id)
            : finalUserId
              ? [finalUserId]
              : [];

        // Send payment to Moyasar
        const paymentBody = {
          amount: toMinorUnits(applePayAmount, tenantCurrency),
          currency: tenantCurrency,
          description:
            selectedAccounts.length > 1
              ? t('moyasarPackageDescMultiAccount', {
                  name: packageData.name,
                  count: selectedAccounts.length,
                })
              : t('moyasarPackageDesc', { name: packageData.name }),
          publishable_api_key: publishableKey,
          source: {
            type: 'applepay',
            token: token,
          },
          // Fixed-slot: send the server-validated recurring-slot metadata
          // verbatim (it already carries type/fixed_slot/purchase_id/package_id/
          // user_id/cadence/price). Generic packages keep the inline build.
          metadata: fixedSlotMetadata ?? {
            type: 'package_purchase',
            package_id: packageData.id,
            purchase_id: purchaseId,
            tenant_id: tenantId || '',
            tenant_name: tenantName || '',
            // Stamp location so the webhook resolves the per-location merchant.
            // Omitted when packageData has no location_id (tenant-wide package).
            ...(locationId && { location_id: locationId }),
            // Include both single user_id (backwards compat) and user_ids array
            ...(userIds.length > 0 && { user_id: userIds[0] }),
            ...(userIds.length > 0 && { user_ids: JSON.stringify(userIds) }),
            ...(userIds.length > 1 && {
              user_count: userIds.length.toString(),
            }),
            ...(userIds.length > 1 && {
              price_per_package: pricePerPackage.toString(),
            }),
            // Discount code (optional). The webhook / return-page re-validate
            // the code and recompute the amount, so we only carry the code and
            // the pre-discount unit price. price_per_package is overridden to
            // the discounted unit so purchase_price lands correct even before
            // re-validation. `displayPrice` (the charged amount) is already
            // discounted above.
            ...(appliedDiscount &&
              userId && {
                discount_code: appliedDiscount.code,
                original_unit_price: pricePerPackage.toString(),
                price_per_package: discountedUnit.toString(),
                // The buyer the code was previewed against — the webhook /
                // return-page re-validate every row against this user (Apple Pay
                // is authenticated-only here, so userId is the buyer).
                discount_acting_user_id: userId,
              }),
            // Granted (no code): Apple Pay charges the client total, so the grant
            // must ride along or the buyer is overcharged (webhook persists it).
            ...(!appliedDiscount &&
              grantedApplied &&
              grantedDiscount &&
              userId && {
                discount_granted_addon_id: grantedDiscount.addonId,
                original_unit_price: pricePerPackage.toString(),
                price_per_package: discountedUnit.toString(),
                discount_acting_user_id: userId,
              }),
            ...(!isAuthenticated && {
              phone_number: customerPhone,
              customer_name: customerName,
              customer_email: customerEmail,
            }),
            // Customer-chosen validity start (book-by, no window). The return
            // page / webhook pass it through as starts_at and the DB trigger
            // derives the enforced window from it. Omitted = start today.
            ...(startDatePayload && { start_date: startDatePayload }),
            // Always include player_count - defaults to 1 for packages without player_pricing
            player_count: (playerCount ?? 1).toString(),
            // Per-player name/phone captured for player-priced packages (2+
            // players). JSON string; the webhook / return page persist it to
            // user_booking_packages.player_details.
            ...(playerDetailsPayload && {
              player_details: JSON.stringify(playerDetailsPayload),
            }),
          },
        };

        const paymentResponse = await fetch(
          'https://api.moyasar.com/v1/payments',
          {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(paymentBody),
          },
        );

        const paymentResult = await paymentResponse.json();

        if (!paymentResponse.ok || paymentResult.status !== 'paid') {
          const failureReason =
            paymentResult.message ||
            paymentResult.source?.message ||
            'Payment failed';
          console.error('[Apple Pay] Package payment failed:', {
            status: paymentResponse.status,
            paymentStatus: paymentResult.status,
            message: paymentResult.message,
            sourceMessage: paymentResult.source?.message,
            sourceType: paymentResult.source?.type,
          });
          captureException(
            new Error(`Apple Pay package payment failed: ${failureReason}`),
            {
              tags: {
                provider: 'moyasar', // Apple Pay always uses Moyasar
                payment_method: 'applepay',
                error_type: 'payment_failed',
                booking_type: 'package',
              },
              extra: {
                httpStatus: paymentResponse.status,
                paymentStatus: paymentResult.status,
                message: paymentResult.message,
                sourceMessage: paymentResult.source?.message,
              },
            },
          );
          toast.error(failureReason);
          safeCompletePayment(
            session,
            (window as any).ApplePaySession.STATUS_FAILURE,
          );
          setIsPaymentProcessing(false);
          return;
        }

        // Payment successful
        safeCompletePayment(
          session,
          (window as any).ApplePaySession.STATUS_SUCCESS,
        );

        toast.success(t('toastPaymentSuccessRedirecting'));
        router.push(
          fixedSlotReturnPath
            ? `${fixedSlotReturnPath}&id=${paymentResult.id}`
            : `/book/return?type=package&purchase_id=${purchaseId}&package_id=${packageData.id}&id=${paymentResult.id}`,
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('toastPaymentProcessingFailed');
        console.error(
          '[Apple Pay] Package payment error:',
          errorMessage,
          error,
        );
        captureException(error, {
          tags: {
            payment_method: 'applepay',
            error_type: 'package_payment_error',
          },
        });
        toast.error(errorMessage);
        safeCompletePayment(
          session,
          (window as any).ApplePaySession.STATUS_FAILURE,
        );
        setIsPaymentProcessing(false);
      }
    };

    // Handle cancellation
    session.oncancel = (event: any) => {
      if (!merchantValidated) {
        console.error(
          '[Apple Pay] Package payment sheet dismissed before merchant validation',
          {
            statusCode: event?.statusCode,
            domain: window.location.hostname,
          },
        );
        captureException(
          new Error(
            'Apple Pay sheet dismissed: merchant validation failed (package)',
          ),
          {
            tags: {
              payment_method: 'applepay',
              error_type: 'session_dismissed_before_validation',
              booking_type: 'package',
            },
            extra: {
              domain: window.location.hostname,
              hasPublishableKey: !!publishableKey,
            },
          },
        );
        toast.error(t('toastApplePayInitFailed'));
      } else {
        console.warn('[Apple Pay] Package payment sheet dismissed by user');
        toast.info(t('toastPaymentCancelled'));
      }
      setIsPaymentProcessing(false);
    };

    // Begin the session
    session.begin();
  };

  // Handle card payment button click
  const handlePayNowClick = () => {
    if (policyBlocked) {
      toast.error(t('toastAcceptPolicyRequired'));
      return;
    }
    // Check if package data exists
    if (!packageData) {
      toast.error(t('toastPackageDataUnavailable'));
      return;
    }

    // Check if player count is selected for packages with player pricing
    if (packageData.player_pricing && playerCount === null) {
      toast.error(t('toastSelectPlayers'));
      return;
    }

    // Require each player's name + phone when 2+ players are selected
    if (shownPlayerRows > 0 && !playerDetailsComplete) {
      toast.error(t('toastPlayerDetailsIncomplete'));
      return;
    }

    // For non-authenticated users with card payment, require sign-in
    if (!isAuthenticated) {
      handleSignIn();
      return;
    }

    if (selectedAccounts.length !== 1) {
      toast.error(t('toastSelectAccount'));
      return;
    }

    // Always pass player count (defaults to 1 for packages without player pricing)
    // Pass the selected recipient using the existing checkout payload shape.
    // Pass pricePerPackage (not displayPrice which is total) — the server
    // re-validates the discount code and applies it to this unit price.
    const discountCode = appliedDiscount?.code;
    if (packageData.player_pricing && playerCount !== null) {
      onCheckout(
        packageData.id,
        playerCount,
        pricePerPackage,
        selectedAccounts,
        discountCode,
        playerDetailsPayload,
        startDatePayload,
      );
    } else {
      onCheckout(
        packageData.id,
        1,
        undefined,
        selectedAccounts,
        discountCode,
        undefined,
        startDatePayload,
      );
    }
  };

  const handleWalletPayClick = async () => {
    if (policyBlocked) {
      toast.error(t('toastAcceptPolicyRequired'));
      return;
    }
    if (!packageData || !onWalletCheckout) {
      toast.error(t('toastPackageDataUnavailable'));
      return;
    }

    if (packageData.player_pricing && playerCount === null) {
      toast.error(t('toastSelectPlayers'));
      return;
    }

    if (shownPlayerRows > 0 && !playerDetailsComplete) {
      toast.error(t('toastPlayerDetailsIncomplete'));
      return;
    }

    if (!isAuthenticated) {
      handleSignIn();
      return;
    }

    if (selectedAccounts.length !== 1) {
      toast.error(t('toastSelectAccount'));
      return;
    }

    if (
      !canPayPackageWithWallet(
        true,
        true,
        walletEnabled,
        walletBalance,
        displayPrice,
      )
    ) {
      toast.error(
        t('toastPackageWalletInsufficient', {
          defaultValue: 'Your wallet must cover the full package price.',
        }),
      );
      return;
    }

    const intentKey = JSON.stringify({
      packageId: packageData.id,
      recipientAccountIds: selectedAccounts.map((account) => account.id),
      playerCount: playerCount ?? 1,
      price: packageData.player_pricing ? pricePerPackage : undefined,
      discountCode: appliedDiscount?.code,
      playerDetails: playerDetailsPayload,
      startDate: startDatePayload,
    });

    // The reference is the debit's idempotency key, so it must survive a
    // reload between the RPC committing and the response landing — a purely
    // in-memory reference would mint a new UUID on retry and double-charge.
    const storageKey = `packageWalletRef:${intentKey}`;

    if (walletPurchaseReferenceRef.current?.intentKey !== intentKey) {
      let reference: string | null = null;
      try {
        reference = window.sessionStorage.getItem(storageKey);
      } catch {
        reference = null;
      }

      if (!reference) {
        reference = crypto.randomUUID();
        try {
          window.sessionStorage.setItem(storageKey, reference);
        } catch {
          // Storage unavailable (private mode / quota) — the in-memory ref
          // still guards same-session retries.
        }
      }

      walletPurchaseReferenceRef.current = { intentKey, reference };
    }

    const succeeded = await onWalletCheckout(
      packageData.id,
      playerCount ?? 1,
      packageData.player_pricing ? pricePerPackage : undefined,
      selectedAccounts,
      appliedDiscount?.code,
      playerDetailsPayload,
      startDatePayload,
      walletPurchaseReferenceRef.current.reference,
    );

    // Only a confirmed success releases the reference; a later identical
    // purchase must mint a fresh one or the RPC's idempotency branch would
    // silently return this settlement instead of selling a second package.
    if (succeeded === true) {
      walletPurchaseReferenceRef.current = null;
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
        // Best-effort cleanup.
      }
    }
  };

  // Apple Pay session must be created synchronously inside a user-gesture
  // handler. Re-attaching the listener on every render (whenever
  // selectedAccounts/playerCount change) churns the listener and breaks
  // Safari's gesture tracking — after one cancel the next tap silently
  // no-ops. The class-payment Apple Pay button uses useEffect([]) because
  // its button is in the DOM from first render. Here the button is
  // conditionally rendered (dialog open + playerCount selected), so we
  // attach via a ref callback that fires on every mount/unmount of the
  // <apple-pay-button> element. The handler is read through a ref so the
  // listener stays bound to the element across state changes while always
  // seeing the latest selectedAccounts.
  const applePayHandlerRef = useRef(handleApplePayClick);
  applePayHandlerRef.current = handleApplePayClick;

  const applePayClickRef = useRef<(() => void) | null>(null);

  const setApplePayButtonRef = (el: HTMLElement | null) => {
    const prev = applePayButtonRef.current;
    if (prev && applePayClickRef.current) {
      prev.removeEventListener('click', applePayClickRef.current);
      applePayClickRef.current = null;
    }
    applePayButtonRef.current = el;
    if (el) {
      const onClick = () => applePayHandlerRef.current();
      el.addEventListener('click', onClick);
      applePayClickRef.current = onClick;
    }
  };

  const calculateTotalSavings = (
    price: number,
    originalPrice: number | null | undefined,
  ) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return originalPrice - price;
  };

  // Early return if no package data
  if (!packageData) return null;

  const totalSavings = calculateTotalSavings(
    packageData.price,
    packageData.original_price,
  );

  // Determine package color based on name or use custom color
  let bgColor = packageData.color;
  if (!bgColor) {
    if (packageData.name.toLowerCase().includes('gold')) {
      bgColor = PACKAGE_COLORS.gold;
    } else if (packageData.name.toLowerCase().includes('silver')) {
      bgColor = PACKAGE_COLORS.silver;
    } else if (packageData.name.toLowerCase().includes('bronze')) {
      bgColor = PACKAGE_COLORS.bronze;
    }
  }

  return (
    <>
      {isOpen ? (
        <TenantBeginCheckout
          contentId={packageData.id}
          contentName={packageData.name}
          contentType="package_purchase"
          value={packageData.price}
        />
      ) : null}
      {/* Apple Pay SDK Script */}
      <Script
        src="https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"
        strategy="lazyOnload"
      />

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          {...presentation}
          // Radix focuses the first tabbable node on open. When the tenant
          // offers a start date that node is the `type="date"` input, and iOS
          // WebKit pops the native date picker the instant it takes focus — so
          // opening a package looked like it opened the calendar.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'max-h-[90vh] overflow-y-auto border-0 bg-transparent p-0 sm:max-w-md',
            presentation.className,
          )}
        >
          <DialogTitle className="sr-only">
            {t('packageCheckoutTitle')}
          </DialogTitle>
          <Card className="relative overflow-hidden border-0 shadow-lg">
            {bgColor && (
              <div
                className="absolute inset-0 opacity-10"
                style={{ backgroundColor: bgColor }}
              />
            )}

            <CardHeader className="relative pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold">{packageData.name}</h4>
                  <p className="text-muted-foreground mt-1 text-sm capitalize">
                    {formatPackageLabel(
                      t,
                      packageData.package_type,
                      packageData.booking_count,
                      packageData.is_bundle,
                    )}
                  </p>
                </div>
                {/* Subscriber limit info */}
                {packageData.max_subscribers !== null &&
                  packageData.max_subscribers !== undefined && (
                    <Badge
                      variant={
                        packageData.remaining_slots === 0
                          ? 'destructive'
                          : 'outline'
                      }
                      className="whitespace-nowrap"
                    >
                      {packageData.remaining_slots === 0
                        ? t('soldOut')
                        : t('slotsRemaining', {
                            remaining: packageData.remaining_slots,
                            max: packageData.max_subscribers,
                          })}
                    </Badge>
                  )}
              </div>
            </CardHeader>

            <CardContent className="relative space-y-4">
              {/* Player Count Dropdown - Only show if package has player pricing */}
              {packageData.player_pricing && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="text-muted-foreground h-4 w-4" />
                    <label className="text-sm font-medium">
                      {t('numberOfPlayersLabel')}
                    </label>
                  </div>
                  <Select
                    value={
                      playerCount !== null ? String(playerCount) : undefined
                    }
                    onValueChange={(value) => {
                      const nextCount = parseInt(value);
                      setPlayerCount(nextCount);
                      resizePlayerDetails(nextCount);
                      // The preview discount was computed against the previous
                      // unit price — drop it so the customer re-applies against
                      // the new player-count price (prevents a stale Apple Pay
                      // charge, since Apple Pay charges the client-side total).
                      setAppliedDiscount(null);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('selectNumberOfPlayers')} />
                    </SelectTrigger>
                    <SelectContent {...presentation}>
                      <SelectItem value="1">
                        <span className="flex items-center gap-1">
                          {t('onePlayer')} <CurrencyIcon className="h-3 w-3" />{' '}
                          {packageData.price.toFixed(2)}
                        </span>
                      </SelectItem>
                      {[2, 3, 4, 5, 6].map((count) => {
                        const price =
                          packageData.player_pricing?.[String(count)];
                        if (price === undefined || price === null) return null;
                        return (
                          <SelectItem key={count} value={String(count)}>
                            <span className="flex items-center gap-1">
                              {t('nPlayers', { count })}{' '}
                              <CurrencyIcon className="h-3 w-3" />{' '}
                              {price.toFixed(2)}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Show all details only when player count is selected or not required */}
              {(playerCount !== null || !packageData.player_pricing) && (
                <>
                  {/* Per-player name + phone — player-priced packages, 2+ players.
                      Captured into user_booking_packages.player_details. */}
                  {shownPlayerRows > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Users className="text-muted-foreground h-4 w-4" />
                        <label className="text-sm font-medium">
                          {t('packages:playerDetails')}
                        </label>
                      </div>
                      {Array.from({ length: shownPlayerRows }).map(
                        (_, index) => (
                          <div
                            key={index}
                            className="space-y-2 rounded-lg border p-3"
                          >
                            <p className="text-muted-foreground text-xs font-medium">
                              {t('packages:playerNumber', { number: index + 1 })}
                            </p>
                            <Input
                              placeholder={t('packages:fullNamePlaceholder')}
                              value={
                                (playerDetails[index]?.name?.trim() &&
                                  playerDetails[index]?.name) ||
                                (index === 0 ? (userName ?? '') : '')
                              }
                              onChange={(e) =>
                                updatePlayerDetail(
                                  index,
                                  'name',
                                  e.target.value,
                                )
                              }
                            />
                            <PhoneInput
                              popoverClassName={presentation.className}
                              popoverStyle={presentation.style}
                              placeholder="Phone number"
                              value={
                                (playerDetails[index]?.phone?.trim() &&
                                  playerDetails[index]?.phone) ||
                                (index === 0 ? (userPhone ?? '') : '')
                              }
                              onChange={(value) =>
                                updatePlayerDetail(index, 'phone', value ?? '')
                              }
                            />
                          </div>
                        ),
                      )}
                    </div>
                  )}

                  {isAuthenticated && userId && (
                    <AccountMultiSelector
                      userId={userId}
                      userName={userName}
                      tenantId={tenantId}
                      selectedAccounts={selectedAccounts}
                      onChange={(accounts) => {
                        setSelectedAccounts(accounts);
                        setAppliedDiscount(null);
                      }}
                      multiple={false}
                    />
                  )}

                  {/* Price. Two DIFFERENT discounts can apply and must never
                      be conflated: the package's own list-vs-sale saving
                      (original_price -> price, a property of the catalog) and a
                      voucher code the customer entered (applied per purchase).
                      The headline is the package price with only the package
                      saving struck through; the voucher's effect belongs to the
                      applied-code chip and the Subtotal/Discount/Total summary
                      below, which already show it line by line. Showing the
                      post-voucher total here struck against the package price
                      made the "savings" figure look wrong. */}
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-2">
                      <CurrencyIcon className="text-muted-foreground h-5 w-5" />
                      <span className="text-3xl font-bold">
                        {(pricePerPackage * accountCount).toFixed(0)}
                      </span>
                      {totalSavings > 0 && (
                        <span className="text-muted-foreground flex items-baseline gap-1 text-base line-through">
                          <CurrencyIcon className="h-3.5 w-3.5 self-center" />
                          {(
                            Number(packageData.original_price) * accountCount
                          ).toFixed(0)}
                        </span>
                      )}
                      {selectedAccounts.length > 1 && (
                        <span className="text-muted-foreground text-sm">
                          ({pricePerPackage.toFixed(0)} ×{' '}
                          {selectedAccounts.length} accounts)
                        </span>
                      )}
                    </div>
                    {totalSavings > 0 && (
                      <p className="flex items-center text-sm text-green-600">
                        {t('packageSavings')}{' '}
                        <CurrencyIcon className="mx-1 h-4 w-4" />
                        {(totalSavings * accountCount).toFixed(0)}
                      </p>
                    )}
                  </div>

                  {/* Discount code — same component the court flow uses, in
                      preview mode. Authenticated only: validate_discount_code
                      reads auth.uid() for membership-gated codes. */}
                  {allowDiscountCodes &&
                    isAuthenticated &&
                    tenantId &&
                    pricePerPackage > 0 && (
                      <DiscountCodeInput
                        mode={{
                          kind: 'preview',
                          accountId: tenantId,
                          draftTotal: pricePerPackage,
                          bookingType: packageData.package_type,
                          // Lets a code scoped to this exact package
                          // (scope_package_id) validate in the preview.
                          packageId: packageData.id,
                          recipientUserId: selectedAccounts[0]?.id ?? userId,
                        }}
                        applied={appliedDiscount}
                        onApplied={setAppliedDiscount}
                        onRemoved={() => setAppliedDiscount(null)}
                        showCancelNote={false}
                        suggestedCodes={suggestedCodes}
                        autoApplySingle
                      />
                    )}

                  {/* Subtotal / discount / total breakdown (only when applied) */}
                  {discountTotal > 0 && (
                    <div className="bg-muted/30 space-y-1 rounded-lg border px-3 py-2 text-sm">
                      <div className="text-muted-foreground flex items-center justify-between">
                        <span>{t('subtotal')}</span>
                        <span className="flex items-baseline gap-1">
                          <CurrencyIcon className="h-3 w-3 self-center" />
                          {subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-green-600">
                        <span>
                          {appliedDiscount
                            ? `${t('discount')} ${appliedDiscount.code ?? ''}`.trim()
                            : t('grantedDiscountApplied', {
                                defaultValue: '{{name}} applied automatically',
                                name: grantedDiscount?.name ?? '',
                              })}
                        </span>
                        <span className="flex items-baseline gap-1">
                          −<CurrencyIcon className="h-3 w-3 self-center" />
                          {discountTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t pt-1 font-semibold">
                        <span>{t('checkoutTotal')}</span>
                        <span className="flex items-baseline gap-1">
                          <CurrencyIcon className="h-3 w-3 self-center" />
                          {displayPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Validity — fixed-window packages show the actual date range
                      (credits only work inside it); others show the duration. */}
                  <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {packageData.fixed_starts_at && packageData.fixed_ends_at
                        ? t('packageValidityWindow', {
                            defaultValue: 'Valid {{start}} – {{end}}',
                            start: formatLocaleDate(
                              packageData.fixed_starts_at,
                              i18n.language,
                            ),
                            end: formatLocaleDate(
                              packageData.fixed_ends_at,
                              i18n.language,
                            ),
                          })
                        : packageData.validity_days
                          ? packageData.validity_days % 30 === 0
                            ? t('expiresIn', {
                                count: packageData.validity_days / 30,
                              })
                            : packageData.validity_days % 7 === 0
                              ? t('expiresInWeeks', {
                                  count: packageData.validity_days / 7,
                                })
                              : t('expiresInDays', {
                                  count: packageData.validity_days,
                                })
                          : t('expiresIn', {
                              count: packageData.validity_months || 7,
                            })}
                    </span>
                  </div>

                  {/* Optional validity start — book-by packages without a fixed
                      window only. Empty/today = start immediately (omitted from
                      the payload); a future date delays when credits open.

                      Collapsed by default: the overwhelming majority of buyers
                      want their credits to start now, and an always-open date
                      field cost three lines of the dialog to say "leave this
                      alone". Keeping the input unmounted until asked for also
                      means it can never be the dialog's first tabbable node,
                      which is what made iOS pop its native date picker the
                      moment the drawer opened. */}
                  {offersStartDate && (
                    <div className="space-y-1">
                      {showStartDate ? (
                        <>
                          <label
                            htmlFor="package-start-date"
                            className="text-sm font-medium"
                          >
                            {t('packageStartDateLabel', {
                              defaultValue: 'Start date (optional)',
                            })}
                          </label>
                          <Input
                            id="package-start-date"
                            type="date"
                            min={todayLocal}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                          <p className="text-muted-foreground text-xs">
                            {t('packageStartDateHint', {
                              defaultValue:
                                'Leave empty to start today. Validity runs from the date you pick.',
                            })}
                          </p>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowStartDate(true)}
                          className="text-muted-foreground hover:text-foreground -mt-2 inline-flex items-center text-sm underline underline-offset-2 transition-colors"
                          data-test="package-start-date-toggle"
                        >
                          {t('packageStartDateToggle', {
                            defaultValue: 'Start on a later date?',
                          })}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  {packageData.description && (
                    <p className="text-muted-foreground text-sm">
                      {packageData.description}
                    </p>
                  )}

                  {/* Features */}
                  {packageData.features && packageData.features.length > 0 && (
                    <ul className="space-y-1">
                      {packageData.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <span className="me-2 text-green-500">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Payment Buttons - Stacked like payment page */}
                  <div className="space-y-3 border-t pt-3">
                    <h5 className="text-sm font-semibold">
                      {t('paymentMethod')}
                    </h5>

                    <BookingPolicyNotice
                      visible={!!bookingPolicy}
                      requireAcceptance={
                        bookingPolicy?.requireAcceptance ?? false
                      }
                      accepted={hasAcceptedPolicy}
                      onAcceptedChange={(accepted) =>
                        setPolicyAcceptance({ context: policyContext, accepted })
                      }
                      cancellationPolicyItems={
                        bookingPolicy?.cancellationPolicyItems ?? []
                      }
                      termsText={bookingPolicy?.termsText ?? ''}
                    >
                    {isAuthenticated &&
                      walletEnabled &&
                      onWalletCheckout &&
                      displayPrice > 0 &&
                      // Zero or negative balance (nothing to spend, or the
                      // customer owes money) hides the wallet tender entirely
                      // — same rule as the /book flow.
                      walletBalance > 0 && (
                        <>
                          {/* Mirrors the wallet option in payment-method-selector
                            (the /book flow): bordered row, emerald wallet glyph,
                            and the balance rendered as a labelled sub-line so it
                            can't be misread as the amount being charged. */}
                          <Button
                            className="h-auto w-full justify-start gap-3 py-3"
                            variant="outline"
                            onClick={() => setIsWalletConfirmOpen(true)}
                            disabled={
                              policyBlocked ||
                              isProcessing ||
                              isPaymentProcessing ||
                              !canPayPackageWithWallet(
                                true,
                                true,
                                walletEnabled,
                                walletBalance,
                                displayPrice,
                              )
                            }
                            data-test="package-wallet-payment-button"
                          >
                            <Wallet className="h-5 w-5 shrink-0 text-emerald-600" />
                            <span className="flex flex-col items-start gap-0.5">
                              <span className="text-base font-medium">
                                {isProcessing
                                  ? t('processing')
                                  : t('payWithWallet')}
                              </span>
                              {!isProcessing && (
                                <span className="text-muted-foreground inline-flex items-baseline gap-1 text-xs font-normal">
                                  {t('walletBalanceLabel', {
                                    defaultValue: 'Current balance',
                                  })}
                                  :
                                  <CurrencyIcon className="h-3 w-3 self-center" />
                                  <span>{walletBalance.toFixed(2)}</span>
                                </span>
                              )}
                            </span>
                          </Button>
                          {walletBalance < displayPrice && (
                            <p className="text-muted-foreground text-center text-xs">
                              {t('toastPackageWalletInsufficient', {
                                defaultValue:
                                  'Your wallet must cover the full package price.',
                              })}
                            </p>
                          )}

                          <AlertDialog
                            open={isWalletConfirmOpen}
                            onOpenChange={setIsWalletConfirmOpen}
                          >
                            <AlertDialogContent {...presentation}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t(
                                    'packagePayment.confirmWalletPaymentTitle',
                                    {
                                      defaultValue: 'Confirm wallet payment',
                                    },
                                  )}
                                </AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-3">
                                    <p>
                                      {t(
                                        'packagePayment.confirmWalletPaymentDescription',
                                        {
                                          defaultValue:
                                            'This will immediately deduct the package price from your wallet. This action cannot be undone from checkout.',
                                        },
                                      )}
                                    </p>
                                    <div className="bg-muted rounded-md p-3 text-sm">
                                      <div className="flex items-center justify-between gap-4">
                                        <span>{packageData.name}</span>
                                        <span className="flex items-center gap-1 font-semibold">
                                          <CurrencyIcon className="h-4 w-4" />
                                          {displayPrice.toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  {t('packagePayment.cancelWalletPayment', {
                                    defaultValue: 'Cancel',
                                  })}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  data-test="confirm-package-wallet-payment"
                                  disabled={policyBlocked}
                                  onClick={() => void handleWalletPayClick()}
                                >
                                  {t(
                                    'packagePayment.confirmWalletPaymentAction',
                                    {
                                      defaultValue: 'Confirm & Pay',
                                    },
                                  )}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}

                    {/* Apple Pay Button — Moyasar-only; only for authenticated
                        users on Moyasar tenants. Other providers offer Apple Pay
                        inside their own checkout. */}
                    {/* Fixed-slot needs the recurring-slot metadata bridge
                        (resolveFixedSlotApplePayMetadata) so the express charge
                        carries the cadence the webhook needs; without the bridge
                        the button stays hidden to avoid a charge-then-refund. */}
                    {isAuthenticated &&
                      provider === 'moyasar' &&
                      (!packageData?.is_fixed_slot ||
                        typeof resolveFixedSlotApplePayMetadata ===
                          'function') && (
                        // dir="ltr": the native <apple-pay-button> otherwise
                        // inherits the page's dir="rtl" (Arabic) and Apple's
                        // shadow-DOM lays the glyph + text right-to-left.
                        <div className="w-full" dir="ltr">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `
                              apple-pay-button {
                                --apple-pay-button-width: 100%;
                                --apple-pay-button-height: 40px;
                                --apple-pay-button-border-radius: var(--native-booking-control-radius, 6px);
                                --apple-pay-button-padding: 0px 0px;
                                --apple-pay-button-box-sizing: border-box;
                                display: block;
                                margin: 0;
                              }
                            `,
                            }}
                          />
                          <apple-pay-button
                            aria-disabled={policyBlocked}
                            ref={setApplePayButtonRef}
                            buttonstyle="black"
                            type="pay"
                            locale="en"
                          />
                        </div>
                      )}

                    {/* Card Payment Button */}
                    <Button
                      className="h-10 w-full text-base"
                      variant="default"
                      onClick={handlePayNowClick}
                      disabled={
                        policyBlocked || isProcessing || isPaymentProcessing
                      }
                    >
                      {isProcessing || isPaymentProcessing
                        ? t('processing')
                        : isAuthenticated
                          ? t('payWithCard')
                          : t('signInToPay')}
                    </Button>
                    </BookingPolicyNotice>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
}
